import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAudio } from '../../../hooks/useAudio';
import { RESULT_TYPE, SE_LIST, RESULT_SHOW_PROGRESS, COMPLETE_TYPE_SCORE, THEMES, NO_SCORE } from '../../../constants/Consts';
import { TypingResult } from '../../../api';
import { ChangeImageWhenHover } from '../../../components/ChangeImageWhenHover';
import { ScoreMeter } from './ScoreMeter';
import { DisplayResultNumber } from './DisplayResultNumber';
import { useTypingResult } from '../../../hooks/api/useTypingResult';
import { WindowExp } from '../../../components/WindowExp';

interface ResultModalProps {
  themeType: THEMES;
  resultType: RESULT_TYPE | null;
  grade: TypingResult;
  score: number;
  correctTypeCount: number;
  missTypeCount: number;
  handleReStart: () => void;
  handleGetStyle: (tailwindStyle: string) => string;
  handleSetResultType: () => void;
}

export const ResultModal = ({
  themeType,
  resultType,
  grade,
  score,
  correctTypeCount,
  missTypeCount,
  handleReStart,
  handleGetStyle,
  handleSetResultType,
}: ResultModalProps) => {
  const navigate = useNavigate();
  const { playSeAudio } = useAudio();
  const { getTypingResults, typingResults, insertTypingResult, updatedTypingResult } = useTypingResult();

  const [resultShowProgress, setResultShowProgress] = useState<number>(0);
  const [scorePercentage, setScorePercentage] = useState<number>(0);
  const [showScoreFrom, setShowScoreFrom] = useState<number | undefined>(undefined);
  const [showScoreTo, setShowScoreTo] = useState<number | undefined>(undefined);
  const [showTypeCount, setShowTypeCount] = useState<number | undefined>(undefined);
  const [showMissTypeCount, setShowMissTypeCount] = useState<number | undefined>(undefined);
  const [showBestScore, setShowBestScore] = useState<number | undefined>(undefined);
  const [showNewRecord, setShowNewRecord] = useState<boolean>(false);
  const [showButtons, setShowButtons] = useState<boolean>(false);

  const { START_BUTTON } = SE_LIST;
  const { gradeNumber, description, limitSeconds, clearScore } = grade;

  const nextGrade: TypingResult | undefined = useMemo(
    () => typingResults?.find((x) => (gradeNumber ? x.gradeNumber === gradeNumber - 1 : false)),
    [gradeNumber, typingResults]
  );
  const currentGrade: TypingResult | undefined = useMemo(
    () => typingResults?.find((x) => x.gradeNumber === gradeNumber),
    [gradeNumber, typingResults]
  );
  const isShowNextGrade: boolean = useMemo(
    () => nextGrade !== undefined && (resultType === RESULT_TYPE.CLEAR || currentGrade?.firstClearedAt !== null),
    [nextGrade, resultType, currentGrade?.firstClearedAt]
  );

  useEffect(() => {
    showResultNumber();
    getTypingResults(themeType);
    insertTypingResult(themeType, gradeNumber, score, correctTypeCount, missTypeCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    showResultNumber();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultShowProgress]);

  const nextResultShowProgress = useCallback(
    (delay?: number): void => {
      setTimeout(() => {
        setResultShowProgress(resultShowProgress + 1);
      }, delay);
    },
    [resultShowProgress]
  );

  const showResultNumber = useCallback((): void => {
    switch (resultShowProgress) {
      case RESULT_SHOW_PROGRESS.TYPE_COUNT_SCORE:
        setShowTypeCount(correctTypeCount);
        setShowScoreFrom(0);
        setShowScoreTo(correctTypeCount * COMPLETE_TYPE_SCORE);
        break;
      case RESULT_SHOW_PROGRESS.MISS_TYPE_COUNT:
        setShowMissTypeCount(missTypeCount);
        setShowScoreFrom(correctTypeCount * COMPLETE_TYPE_SCORE);
        setShowScoreTo(score);
        break;
      case RESULT_SHOW_PROGRESS.SCORE_METER: {
        if (!score) {
          // スコアが0なら、エフェクトをスキップ
          nextResultShowProgress();
          return;
        }
        let percentage = (score / clearScore) * 100;
        if (percentage > 100) {
          percentage = 100;
        }
        setScorePercentage(percentage);
        nextResultShowProgress(2000);
        break;
      }
      case RESULT_SHOW_PROGRESS.NEW_RECORD:
        if (currentGrade?.bestScore && currentGrade.bestScore >= score) {
          // ベストスコアじゃないなら、エフェクトをスキップ
          nextResultShowProgress();
          return;
        }
        setShowBestScore(score);
        setShowNewRecord(true);
        nextResultShowProgress(500);
        break;
      case RESULT_SHOW_PROGRESS.RESULT_TYPE:
        handleSetResultType();
        nextResultShowProgress(500);
        break;
      case RESULT_SHOW_PROGRESS.BUTTONS:
        setShowButtons(true);
        break;
      default:
        break;
    }
  }, [resultShowProgress, clearScore, missTypeCount, correctTypeCount, score, currentGrade, nextResultShowProgress, handleSetResultType]);

  const resetShowItems = useCallback((): void => {
    setResultShowProgress(0);
    setScorePercentage(0);
    setShowScoreFrom(undefined);
    setShowScoreTo(undefined);
    setShowTypeCount(undefined);
    setShowMissTypeCount(undefined);
    setShowBestScore(undefined);
    setShowNewRecord(false);
    setShowButtons(false);
    handleReStart();
  }, [handleReStart]);

  return (
    <>
      <div className="relative pt-[1%] mx-[1%] flex flex-col items-center aspect-[13/5] border-4 border-[#cb9b0a] bg-[#fedaa8] rounded-2xl select-none">
        <p className={`text-main_text font-black ${handleGetStyle('text-lg tb:text-xl pc:text-2xl')}`}>
          {limitSeconds}びょう スコア{clearScore}でクリア
        </p>
        <p className={`text-sub_text font-black ${handleGetStyle('text-base tb:text-lg pc:text-xl')}`}>{description}</p>
        <img className="absolute w-[50%] top-[22%]" src={`/common/result_frame.webp`} alt={'結果フレーム'} />
        {showNewRecord && <img className="absolute w-[20%] top-[18%] animate-fade-in-short" src={`/common/new_record_tag.webp`} alt={'新記録'} />}
        <div className={`relative w-[35%] justify-around flex flex-col font-bold ${handleGetStyle('mt-[2%] tb:mt-[3%] pc:mt-[4%]')}`}>
          <div className="w-full flex">
            <div className="w-full flex flex-col">
              <span className={`text-sub_text ${handleGetStyle('text-base tb:text-lg pc:text-xl')}`}>スコア</span>
              <p className={`text-main_text ${handleGetStyle('text-4xl tb:text-5xl pc:text-6xl')}`}>
                <DisplayResultNumber resultNumber={showScoreTo} fromNumber={showScoreFrom} />
              </p>
            </div>
            <div className="w-full flex flex-col">
              <div className="flex items-end">
                <span className={`w-[60%] text-center text-sub_text ${handleGetStyle('text-base tb:text-lg pc:text-xl')}`}>タイプ</span>
                <p className={`text-main_text ${handleGetStyle('text-2xl tb:text-3xl pc:text-4xl')}`}>
                  <DisplayResultNumber resultNumber={showTypeCount} handleCallbackAnimation={() => nextResultShowProgress(500)} />
                </p>
                <span className={`text-main_text ${handleGetStyle('text-base tb:text-lg pc:text-xl')}`}>回</span>
              </div>
              <div className="flex items-end">
                <span className={`w-[60%] text-center text-sub_text ${handleGetStyle('text-base tb:text-lg pc:text-xl')}`}>ミス</span>
                <p className={`text-main_text ${handleGetStyle('text-2xl tb:text-3xl pc:text-4xl')}`}>
                  <DisplayResultNumber resultNumber={showMissTypeCount} handleCallbackAnimation={() => nextResultShowProgress(500)} />
                </p>
                <span className={`text-main_text ${handleGetStyle('text-base tb:text-lg pc:text-xl')}`}>回</span>
              </div>
            </div>
          </div>
          <ScoreMeter scorePercentage={scorePercentage} />
          <hr className="border-t-2 border-gray-300 mt-[2%] mb-[1%]" />
          <div className="w-full flex justify-around">
            <span className={`text-center text-sub_text ${handleGetStyle('text-base tb:text-lg pc:text-xl')}`}>
              ぜんかい：{updatedTypingResult?.playedScores[1] ?? NO_SCORE}
            </span>
            <span className={`text-center text-sub_text ${handleGetStyle('text-base tb:text-lg pc:text-xl')}`}>
              さいこう：{showBestScore ?? currentGrade?.bestScore ?? NO_SCORE}
            </span>
          </div>
        </div>
        {updatedTypingResult?.expPoint && updatedTypingResult?.expType && (
          <WindowExp expPoint={updatedTypingResult.expPoint} expType={updatedTypingResult.expType} />)}
        {showButtons && (
          <div className="absolute bottom-[18%] w-[60%] flex items-center justify-around">
            <ChangeImageWhenHover
              handleClick={() => {
                resetShowItems();
                playSeAudio(START_BUTTON);
                handleReStart();
              }}
              frameClassName={`w-[30%] ${!isShowNextGrade ? 'animate-emphasis-infinite' : ''}`}
              src={'/common/buttons/once_more_button'}
              alt={'もう一度'}
            />
            {isShowNextGrade && (
              <ChangeImageWhenHover
                handleClick={() => {
                  resetShowItems();
                  playSeAudio(START_BUTTON);
                  navigate(`/practice/${themeType}/${nextGrade?.gradeNumber}/`, { state: { reset: true } });
                }}
                frameClassName={`w-[30%] ${isShowNextGrade ? 'animate-emphasis-infinite' : ''}`}
                src={'/common/buttons/next_grade_button'}
                alt={'次のランクへ'}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};
